import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from '../../../components/shared/Modal/Modal';
import { useTranslation } from '../../../hooks/useTranslation';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { shouldBlockNewChatAgent } from '../../../helpers/pricingAgentLimits';
import Company from '../../../interfaces/Company';
import ViewAgentDashboardDialog from '../../admin/ViewAgentDashboardDialog';
import StyledButton from '../../shared/Button/StyledButton';
import StyledTable from '../../shared/Table/StyledTable';
import StyledTableHeader from '../../shared/Table/StyledTableHeader';
import Table from './Table';
import { Stack } from '@mui/material';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';

interface ChatAgentTableProps {
  companyDetails: Company | undefined;
}

const ChatAgentTable: React.FC<ChatAgentTableProps> = ({ companyDetails }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { agentsCount } = useContext(DashboardContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const atV2FreeAgentLimit = shouldBlockNewChatAgent(
    companyDetails,
    agentsCount
  );

  return (
    <StyledTable>
      <StyledTableHeader>
        <h1>{t('Chat Agents')}</h1>
        <Stack direction="row" gap={2}>
          {currentUser?.permissions?.super_admin && (
            <>
              <StyledButton
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => setDialogOpen(true)}
              >
                {t('View Agent Dashboard')}
              </StyledButton>
              <ViewAgentDashboardDialog
                agentType="chat"
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
              />
            </>
          )}
          {currentUser &&
            canPerformAction(currentUser, PERMISSIONS.VIEW_AGENTS_DASHBOARD) &&
            (companyDetails?.name ? (
              atV2FreeAgentLimit ? (
                <Modal
                  mainPromptText={t('Free plan agent limit')}
                  description={t('Free plan agent limit description')}
                  confirmText={t('View plans')}
                  denyText={t('Cancel')}
                  confirmButtonColor="primary"
                  showButton={true}
                  openModalButtonVariant="contained"
                  openModalButtonColor="primary"
                  openModalButtonText={t('New Agent')}
                  callback={() => navigate('/pricing')}
                />
              ) : (
                <Link to="/agents/chat/new">
                  <StyledButton
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    {t('New Agent')}
                  </StyledButton>
                </Link>
              )
            ) : (
              <Modal
                mainPromptText={t('Company details missing')}
                denyText={t('Cancel')}
                confirmText={t('Setup up company details')}
                confirmButtonColor="primary"
                description={t(
                  'In order to create an agent please fill up the company information.'
                )}
                showButton={true}
                openModalButtonVariant="contained"
                openModalButtonColor="primary"
                openModalButtonText={t('New Agent')}
                callback={() => navigate('/settings')}
              />
            ))}
        </Stack>
      </StyledTableHeader>
      <Table />
    </StyledTable>
  );
};

export default ChatAgentTable;
